import { Button, Grid, GridItem, Link, TextField } from '@flos/react-ui';
import CoverableDetailsTemplate from '../../../../../templates/CoverableDetailsTemplate';
import WizardPagetemplate from '../../../../../templates/WizardPagetemplate';
import { useRecoilValue } from 'recoil';
import type { CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { useSaveCoverableInformation } from '../../../../../hooks/useSaveCoverableInformation';
import { useState } from 'react';
import { validateUVPRegNumber } from '../../../../../utils/coverablesUtil';
import { useHippoText } from '../../../../../hooks/useHippoText';
import { useGetDMRCase } from '../../../../../hooks/useGetDMRInfo';
import { useUpdateCoverableInformation } from '../../../../../recoil/hooks/useUpdateCoverableInformation';
import type { TrailerOnsaDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/uvp';
import TrailerInfoBox from './TrailerInfoBox';
import { track } from '../../../../../utils/trackingUtil';
import { currentOnsaCoverableSelector } from '../../../../../recoil/selectors/currentOnsaCoverableSelector';

const TRAILER_TYPE = 'Påhængsvogn';

const CalculatePriceForTrailerStep = ({
    goNext,
    renderNoRegNumberPage
}: {
    goNext: () => void;
    renderNoRegNumberPage: () => void;
}): React.JSX.Element => {
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const { getDMRCase } = useGetDMRCase();
    const getHippoText = useHippoText();
    const coverable = useRecoilValue<TrailerOnsaDTO>(currentOnsaCoverableSelector);
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const [registrationNumber, setRegistrationNumber] = useState<string>(coverable.regNumber ?? '');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isDMRLoading, setIsDMRLoading] = useState<boolean>(false);
    const [isRegNumberValid, setIsRegNumberValid] = useState<boolean | undefined>(
        coverable.regNumber ? validateUVPRegNumber(registrationNumber) : undefined
    );
    const [isTrailerFound, setIsTrailerFound] = useState<boolean | undefined>(
        coverable.regNumber ? true : undefined
    );

    const fetchDMRInformation = async (registrationNumber: string): Promise<void> => {
        setIsDMRLoading(true);
        await getDMRCase(registrationNumber).then((dmrData) => {
            const { model, make, variant, type } = dmrData;
            if (model && make && variant && type === TRAILER_TYPE) {
                updateCoverableInformation({
                    ...dmrData,
                    isAddedInOnsa: coverable.isAddedInOnsa
                });
                setIsTrailerFound(true);
            } else {
                setIsTrailerFound(false);
            }
        });
        setIsDMRLoading(false);
    };

    const onNext = (): void => {
        setIsLoading(true);
        updateCoverableInformation({
            name: `${coverable.make ?? ''} ${coverable.model ?? ''}, ${registrationNumber}`,
            regNumber: registrationNumber
        });
        const updatedCoverable: CoverableInformationToUpdateDTO = {
            trailer: {
                publicId: coverable.publicId,
                fixedId: coverable.fixedId,
                regNumber: registrationNumber
            },
            productToUpdate: coverable.flavourName
        };
        void saveCoverableInformation(updatedCoverable).then(() => {
            track(
                `${coverable.flavourName}CoverableInformationTrailerInformationWithLicensePlate`,
                [
                    { registrationNumber: coverable.regNumber },
                    { make: coverable.make },
                    { model: coverable.model },
                    { modelYear: coverable.year },
                    { totalWeight: coverable.totalWeight }
                ]
            );
            setIsLoading(false);
            goNext();
        });
    };

    return (
        <WizardPagetemplate
            onNext={onNext}
            disableNext={!isRegNumberValid || !isTrailerFound || isLoading || isDMRLoading}
            isNextLoading={isLoading}
            hidePrevious
        >
            <CoverableDetailsTemplate>
                <Grid>
                    <GridItem colStart={1} span={8}>
                        <p>
                            {getHippoText(
                                'DSS-COVERABLE-INFORMATION-STEP-TRAILER-LICENSE-PLATE-SUBTITLE'
                            )}
                        </p>
                        <TextField
                            label={getHippoText(
                                'DSS-COVERABLE-INFORMATION-STEP-LICENSE-PLATE-ENTER-LICENSE-PLATE'
                            )}
                            value={registrationNumber}
                            onChange={(event) => {
                                setRegistrationNumber(event.target.value?.toUpperCase());
                                setIsRegNumberValid(validateUVPRegNumber(event.target.value));
                            }}
                            isValid={isRegNumberValid && isTrailerFound}
                            disabled={isLoading || isDMRLoading}
                            errorText="Registreringsnr. er ikke gyldigt"
                            minLength={6}
                            maxLength={6}
                        />
                        <div className="u-display-flex@md u-flex-justify-between">
                            <Button
                                className="u-spacing-stack-m"
                                disabled={!isRegNumberValid || isLoading || isDMRLoading}
                                isLoading={isDMRLoading}
                                onClick={() => {
                                    void fetchDMRInformation(registrationNumber);
                                }}
                            >
                                {getHippoText(
                                    'DSS-COVERABLE-INFORMATION-STEP-LICENSE-PLATE-FIND-TRAILER'
                                )}
                            </Button>

                            <Link
                                theme="logo"
                                className={
                                    isLoading || isDMRLoading
                                        ? 'u-spacing-stack-top-6 u-color-supplement-grey-medium'
                                        : 'u-spacing-stack-top-6'
                                }
                                onClick={() => {
                                    if (isLoading || isDMRLoading) return;
                                    renderNoRegNumberPage();
                                }}
                            >
                                {getHippoText(
                                    'DSS-COVERABLE-INFORMATION-STEP-LICENSE-PLATE-DONT-KNOW-LICENSE-PLATE'
                                )}
                            </Link>
                        </div>
                    </GridItem>
                    <GridItem span={4}>
                        <TrailerInfoBox isTrailerFound={isTrailerFound} coverable={coverable} />
                    </GridItem>
                </Grid>
            </CoverableDetailsTemplate>
        </WizardPagetemplate>
    );
};

export default CalculatePriceForTrailerStep;
